import { format } from 'date-fns';
import { memo, useMemo } from 'react';
import { useNavigate, Link as RouterLink } from '@tanstack/react-router';
import {
  defaultTableStyles,
  getDefaultTableOptions,
  serverSideTableOptions,
  defaultPaginationStyles,
} from '@emfood/shared';
import {
  MRT_Updater,
  MRT_SortingState,
  MaterialReactTable,
  type MRT_ColumnDef,
  MRT_PaginationState,
  useMaterialReactTable,
} from 'material-react-table';

import { Box, Link, Stack, Typography } from '@mui/material';

import { paths } from 'src/routes/paths';

import { LocalProductDetailsObject } from 'src/api/models/products';
import { FavouritesDataModel, DetailsFavouritesCollectionModel } from 'src/api/models/favourites';

import PriceLabel from 'src/components/products/price-label';
import SurplusProductLabel from 'src/components/products/surplus-product-label';
import ProductQualityBadges from 'src/components/products/product-quality-badges';
import PromotionalProductLabel from 'src/components/products/promotional-product-label';

import { QuantityCell } from './quantity-cell';
import { ActionsCell, ActiveOperations } from './action-cell';

interface Props {
  products: LocalProductDetailsObject[];
  productsTotalCount?: number;
  paginationModel: MRT_PaginationState;
  sortModel: MRT_SortingState;
  onPaginationModelChanged: (updater: MRT_Updater<MRT_PaginationState>) => void;
  onSortChanged: (updater: MRT_Updater<MRT_SortingState>) => void;
  isLoading?: boolean;
  personalFavouritesCollections?: DetailsFavouritesCollectionModel[];
  favouritesCollectionsData?: FavouritesDataModel;
  isFavouritesLoading?: boolean;
  favouritesCollectionsError?: unknown;
  handleAddToCart: (productStockId: string, quantity: number, rowId: string) => void;
  handleUpdate: (cartItemId: string, quantity: number, rowId: string) => void;
  handleRemoveFromCart: (cartItemId: string, rowId: string) => void;
  handleQuantityChange: (productStockId: string, delta: number) => void;
  activeOperations: ActiveOperations;
}

function ProductListMRTTable({
  products = [],
  productsTotalCount,
  paginationModel,
  sortModel,
  onPaginationModelChanged,
  onSortChanged,
  isLoading,
  personalFavouritesCollections,
  favouritesCollectionsData,
  isFavouritesLoading,
  favouritesCollectionsError,
  handleAddToCart,
  handleUpdate,
  handleRemoveFromCart,
  handleQuantityChange,
  activeOperations,
}: Props) {
  const navigate = useNavigate();

  const columns = useMemo<MRT_ColumnDef<LocalProductDetailsObject>[]>(
    () => [
      {
        id: 'productName',
        accessorKey: 'name',
        header: 'Product',
        size: 200,
        Cell: ({ row }) => (
          <Stack
            alignItems="center"
            direction="row"
            justifyContent="start"
            gap={2}
            sx={{ width: '100%', pl: 2 }}
          >
            {/* Display surplus / promotion icon where relevant */}
            {row.original.surplusEntries?.typeOfPrice === 'surplus' && <SurplusProductLabel />}
            {row.original.surplusEntries?.typeOfPrice === 'promotion' && (
              <PromotionalProductLabel />
            )}
            <Link
              component={RouterLink}
              href={paths.dashboard.products.productDetails(row.original.productStockId)}
            >
              <Typography variant="body2" color="textPrimary" align="left">
                {row.original.name}
              </Typography>
              <Typography variant="body2" color="GrayText">
                {row.original.units}
              </Typography>
            </Link>
            <Box sx={{ ml: 'auto' }}>
              <ProductQualityBadges
                certifications={row.original.certifications}
                dietaryLifestyles={row.original.dietaryLifestyles}
                isLocalProducer={row.original.isLocalProducer}
              />
            </Box>
          </Stack>
        ),
      },
      {
        accessorKey: 'producer',
        header: 'Producer',
        size: 120,
        maxSize: 190,
        enableSorting: false,
        muiTableBodyCellProps: ({ row }) => ({
          align: !row.original.brand && !row.original.producerId ? 'center' : 'left',
        }),
        Cell: ({ row }) => {
          if (row.original.producerId) {
            return (
              <Link
                component="button"
                onClick={() => {
                  navigate({
                    to: paths.dashboard.producer.producerDetails(row.original.producerId!),
                  });
                }}
              >
                <Typography variant="body2" color="textPrimary" align="left">
                  {row.original.producer}
                </Typography>
              </Link>
            );
          }
          return <Typography variant="body2">{row.original.brand || '---'}</Typography>;
        },
      },
      {
        id: 'supplier',
        accessorFn: (row) => row.distributorOrg?.name ?? '---',
        header: 'Supplier',
        size: 120,
        maxSize: 190,
        enableSorting: false,
        Cell: ({ row }) => (
          <Stack spacing={0.25}>
            <Typography variant="body2">{row.original.distributorOrg?.name ?? '---'}</Typography>
            {row.original.distributorReference && (
              <Typography variant="caption" color="text.secondary">
                Ref: {row.original.distributorReference}
              </Typography>
            )}
          </Stack>
        ),
      },
      {
        accessorKey: 'earliestDeliveryDate',
        header: 'Available From',
        size: 120,
        Cell: ({ row }) => (
          <Typography variant="body2">
            {row.original.earliestDeliveryDate
              ? format(new Date(row.original.earliestDeliveryDate), 'dd/MM/yyyy')
              : '---'}
          </Typography>
        ),
      },
      {
        accessorKey: 'price',
        header: 'Price',
        size: 100,
        maxSize: 100,
        Cell: ({ row }) => (
          <PriceLabel
            surplusEntry={row.original.surplusEntries}
            price={row.original.price}
            isEstimate={row.original.weightType === 'Guide Weight'}
          />
        ),
      },
      {
        accessorKey: 'pricePerStandardUnit',
        header: '£ per unit',
        size: 120,
        maxSize: 120,
        Cell: ({ cell }) => (
          <Typography variant="body2" aria-label={`Price per standard unit: ${cell.getValue()}`}>
            {cell.getValue<string>()}
          </Typography>
        ),
      },
      {
        id: 'quantity',
        header: 'Quantity',
        size: 180,
        maxSize: 180,
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ row }) => (
          <QuantityCell
            params={{ row: row.original }}
            handleQuantityChange={handleQuantityChange}
          />
        ),
      },
      {
        id: 'actions',
        header: '',
        size: 160,
        maxSize: 160,
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ row }) => (
          <ActionsCell
            params={{ row: row.original }}
            handleAddToCart={handleAddToCart}
            handleUpdate={handleUpdate}
            handleRemoveFromCart={handleRemoveFromCart}
            activeOperations={activeOperations}
            personalFavouritesCollections={personalFavouritesCollections}
            favouritesCollectionsData={favouritesCollectionsData}
            isFavouritesLoading={isFavouritesLoading}
            favouritesCollectionsError={favouritesCollectionsError}
          />
        ),
      },
    ],
    [
      navigate,
      handleQuantityChange,
      handleAddToCart,
      handleUpdate,
      handleRemoveFromCart,
      activeOperations,
      personalFavouritesCollections,
      favouritesCollectionsData,
      isFavouritesLoading,
      favouritesCollectionsError,
    ]
  );

  const table = useMaterialReactTable({
    ...getDefaultTableOptions(),
    ...defaultTableStyles,
    ...serverSideTableOptions,
    ...defaultPaginationStyles,
    columns,
    data: products,
    memoMode: 'cells',
    getRowId: (row) => row.productStockId,
    enableSorting: true,
    sortDescFirst: false,
    rowCount: productsTotalCount ?? 0,
    state: {
      isLoading,
      pagination: paginationModel,
      sorting: sortModel,
    },
    onPaginationChange: onPaginationModelChanged,
    onSortingChange: onSortChanged,
    muiTableProps: {
      sx: {
        ...defaultTableStyles.muiTableProps.sx,
        // Narrow the actions column ~10% (160 → 144) at the same breakpoint where the
        // wrapping Card drops its horizontal padding. MRT writes these CSS variables
        // as inline styles on the <table>, so the override needs !important to win.
        '@media (max-width:1467px)': {
          '--header-actions-size': '144 !important',
          '--col-actions-size': '144 !important',
        },
      },
    },
  });

  return <MaterialReactTable table={table} />;
}

export default memo(ProductListMRTTable);
